import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../supabase.js';
import { formatDay, loadCampaignEmails } from './adminData.js';
import { baseSubject, readableText } from './emailText.js';
import { SortableTable } from './SortableTable.jsx';
import { formatWhen } from './staffAdmin.js';

const noRows = [];
const time = (value) => Date.parse(value) || 0;
/** Things in a row that do their own job when clicked, so they must not also open or close the conversation. */
const ownClicks = 'a, button, input, select, textarea, label, details';

/**
 * One row per organisation, from a campaign's emails (oldest first): who we emailed and when, their replies (automatic ones apart,
 * as they don't count) and the whole conversation.
 */
export function byOrganisation(emails) {
  const groups = new Map();
  for (const email of emails) {
    if (!groups.has(email.organisation)) groups.set(email.organisation, { organisation: email.organisation, emails: [], emailed: [], lastSentAt: '', replies: [], automatic: 0 });
    const group = groups.get(email.organisation);
    group.emails.push(email);
    if (email.direction === 'sent') {
      if (!group.emailed.includes(email.address)) group.emailed.push(email.address);
      if (time(email.sent_at) > time(group.lastSentAt)) group.lastSentAt = email.sent_at;
    } else if (email.automatic) group.automatic += 1;
    else group.replies.push(email);
  }
  return [...groups.values()].map((group) => {
    const last = group.replies.at(-1);
    return { ...group, lastReplyAt: last?.sent_at ?? '', repliedBy: last ? (last.sender_name || last.address) : '', replyAddress: last?.address ?? '' };
  });
}

/** Where the email can be opened, to reply to it: the Gmail it was copied from. */
const gmailLink = (email) => (email.mailbox && email.thread_id
  ? `https://mail.google.com/mail/u/${encodeURIComponent(email.mailbox)}/#all/${encodeURIComponent(email.thread_id)}`
  : '');

function Mail({ email, threadSubject }) {
  const words = readableText(email.body || '');
  const sent = email.direction === 'sent';
  const link = gmailLink(email);
  return (
    <section className="admin-mail" aria-label={`${sent ? 'Our email' : email.automatic ? 'Automatic reply' : 'Reply'} of ${formatWhen(email.sent_at)}`}>
      <p className="admin-mail-meta">
        <span className="admin-mail-from">{sent ? email.mailbox : (email.sender_name || email.address)}</span>
        {` → ${sent ? email.address : email.mailbox}`}
        {` · ${formatWhen(email.sent_at)}`}
        {email.automatic && ' · Automatic reply'}
        {link && <>{' · '}<a className="admin-ext" href={link} target="_blank" rel="noreferrer">Open in Gmail</a></>}
      </p>
      {baseSubject(email.subject) !== baseSubject(threadSubject) && <p className="admin-mail-subject">{email.subject || 'No subject'}</p>}
      {words ? <p className="admin-mail-text">{words}</p> : <p className="mod-note">This email has no text.</p>}
    </section>
  );
}

/** The whole conversation with one organisation, newest first: the subject we wrote under once, then each email. */
function Conversation({ group }) {
  const subject = group.emails.find((email) => email.direction === 'sent')?.subject || group.emails[0]?.subject || 'No subject';
  return (
    <div className="admin-email-chain">
      <h3 className="admin-subsection">{subject}</h3>
      {[...group.emails].reverse().map((email) => <Mail key={email.id} email={email} threadSubject={subject}/>)}
    </div>
  );
}

/**
 * A campaign's emails, copied in from Gmail (supabase/migrations/202610290020_campaign_emails.sql), as two sections like the
 * Outreach tab: the organisations who replied (newest reply first), then the ones emailed and not heard back from. Either row
 * opens the whole conversation. `name` heads both sections: "Sponsorship emails" and "Sponsorship outreach".
 */
export default function CampaignEmails({ campaign, name }) {
  const [state, setState] = useState({ emails: null, error: '' });
  // The organisation whose conversation is opened under its row ('' when none is).
  const [openId, setOpenId] = useState('');

  useEffect(() => {
    let live = true;
    loadCampaignEmails(supabase, campaign).then(
      (emails) => { if (live) setState({ emails: Array.isArray(emails) ? emails : [], error: '' }); },
      (cause) => { if (live) setState({ emails: null, error: cause.message || `Could not load the ${name.toLowerCase()} emails. Please try again.` }); },
    );
    return () => { live = false; };
  }, [campaign, name]);

  const { emails, error } = state;
  const groups = useMemo(() => byOrganisation(emails ?? noRows), [emails]);
  const replied = useMemo(() => groups.filter((group) => group.replies.length > 0).sort((a, b) => time(b.lastReplyAt) - time(a.lastReplyAt)), [groups]);
  const waiting = useMemo(() => groups.filter((group) => group.replies.length === 0).sort((a, b) => time(b.lastSentAt) - time(a.lastSentAt)), [groups]);

  if (error) return <p className="mod-error" role="alert">{error}</p>;
  if (!emails) return <p>{`Loading ${name.toLowerCase()} emails…`}</p>;
  if (emails.length === 0) return <p className="admin-empty">{`No ${name.toLowerCase()} emails yet.`}</p>;

  /** The whole row opens the conversation; the button is how a keyboard or screen reader does the same (its click bubbles to the row). */
  const rowProps = (group) => {
    const open = openId === group.organisation;
    return {
      className: `admin-email-row${open ? ' is-open' : ''}`,
      onClick: (event) => {
        const own = event.target.closest?.(ownClicks);
        if (own && !own.classList.contains('admin-email-toggle')) return;
        setOpenId(open ? '' : group.organisation);
      },
    };
  };
  const organisation = {
    key: 'organisation',
    label: 'Organisation',
    value: (group) => group.organisation,
    cell: (group) => (
      <span className="admin-nowrap">
        <button type="button" className="admin-email-toggle" aria-expanded={openId === group.organisation} aria-label={`Show the emails with ${group.organisation}`}>
          <span aria-hidden="true">{openId === group.organisation ? '▾' : '▸'}</span>
        </button>
        {group.organisation}
      </span>
    ),
  };
  const replyColumns = [
    organisation,
    { key: 'by', label: 'Replied', value: (group) => group.repliedBy, cell: (group) => group.repliedBy },
    { key: 'address', label: 'Email', value: (group) => group.replyAddress, cell: (group) => group.replyAddress },
    { key: 'last', label: 'Last reply', value: (group) => group.lastReplyAt, first: 'desc', cell: (group) => formatWhen(group.lastReplyAt) },
  ];
  const waitingColumns = [
    organisation,
    { key: 'emailed', label: 'Emailed', value: (group) => group.emailed.join(', '), cell: (group) => group.emailed.join(', ') },
    { key: 'sent', label: 'Sent', value: (group) => group.lastSentAt, first: 'desc', cell: (group) => formatDay(group.lastSentAt) },
    { key: 'heard', label: 'Heard back', value: (group) => group.automatic, cell: (group) => (group.automatic > 0 ? 'Automatic reply only' : 'Nothing yet') },
  ];
  const detail = (group) => (openId === group.organisation ? <Conversation group={group}/> : null);

  return (
    <>
      <h2 className="admin-section">{`${name} emails`}</h2>
      {replied.length === 0
        ? <p className="admin-empty">No replies yet.</p>
        : <SortableTable columns={replyColumns} rows={replied} rowKey={(group) => group.organisation} rowProps={rowProps} detail={detail}/>}
      <h2 className="admin-section">{`${name} outreach`}</h2>
      {waiting.length === 0
        ? <p className="admin-empty">Everyone emailed has replied.</p>
        : <SortableTable columns={waitingColumns} rows={waiting} rowKey={(group) => group.organisation} rowProps={rowProps} detail={detail}/>}
    </>
  );
}
